import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChallenge } from '../hooks/challenge/useChallenge'
import { useChallengeId } from '../hooks/challenge/useChallengeId'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { useTeam } from '../hooks/team/useTeam'
import {
  challengeTeamCreatePath,
  challengeTeamFreeAgentPath,
  challengeTeamJoinPath,
  ScreenPath,
} from '../navigation'
import AppIcon from '../ui/icons/AppIcon'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import OptionList, { OptionItem } from '../ui/primitives/OptionList'
import LoadingIndicator from '../ui/util/LoadingIndicator'
import styles from '../ui/team/TeamEntry.module.css'

export default function TeamIntroScreen() {
  const navigate = useNavigate()
  const challengeId = useChallengeId()
  const { data: challenge, isLoading: isLoadingChallenge } = useChallenge(challengeId)
  const { teamID, isFreeAgent, isLoading } = useChallengeAccess(challengeId)
  const { data: team, isLoading: isLoadingTeam } = useTeam(teamID)

  useEffect(() => {
    if (isLoading || isLoadingTeam) return
    if (!challengeId) {
      navigate(ScreenPath.challenges, { replace: true })
      return
    }
    if (teamID && team) navigate(`${ScreenPath.team}/${teamID}`, { replace: true })
  }, [isLoading, isLoadingTeam, challengeId, teamID, team, navigate])

  if (isLoading || isLoadingChallenge || (teamID && isLoadingTeam)) {
    return <LoadingIndicator />
  }

  return (
    <SimplePageLayout
      title={isFreeAgent ? 'Free agent' : 'Team'}
      backTo={isFreeAgent ? ScreenPath.home : ScreenPath.challenges}
    >
      <div className={styles.stack}>
        {isFreeAgent && <FreeAgentStatus />}
        {isFreeAgent && (
          <div className={styles.orDivider}>
            <span>OR</span>
          </div>
        )}
        <TeamOptionsView
          challengeId={challengeId}
          isFreeAgent={isFreeAgent}
          allowsFreeAgent={challenge?.allowsFreeAgent === true}
        />
      </div>
    </SimplePageLayout>
  )
}

function FreeAgentStatus() {
  return (
    <div className={styles.statusCard}>
      <span className={styles.statusIcon} aria-hidden>
        <AppIcon icon="mdi:check" size={24} />
      </span>
      <h2 className={styles.statusTitle}>You&apos;re signed up as a Free Agent!</h2>
      <p className={styles.statusCopy}>
        We&apos;ll match you with a team before the challenge starts, and send you an email. Or join
        or create a team yourself below.
      </p>
    </div>
  )
}

function TeamOptionsView({
  challengeId,
  isFreeAgent,
  allowsFreeAgent,
}: {
  challengeId: string
  isFreeAgent: boolean
  allowsFreeAgent: boolean
}) {
  const navigate = useNavigate()

  const options: OptionItem[] = [
    {
      title: 'Create a team',
      description:
        'If you know 3 or more people ready for Relentless, create a team and invite them. They can help you build your squad',
      icon: 'mdi:account-multiple-plus-outline',
      tone: 'accent',
      onClick: () => navigate(challengeTeamCreatePath(challengeId)),
    },
    {
      title: 'Join a team',
      description: 'Enter a team code or open the invite link from your leader',
      icon: 'mdi:account-group-outline',
      onClick: () => navigate(challengeTeamJoinPath(challengeId)),
    },
  ]

  if (!isFreeAgent && allowsFreeAgent) {
    options.push({
      title: 'Sign up as a Free Agent',
      description:
        "Don't have a team yet? Sign up as a free agent and we'll match you with a team before the challenge starts.",
      icon: 'mdi:account-outline',
      tone: 'muted',
      onClick: () => navigate(challengeTeamFreeAgentPath(challengeId)),
    })
  }

  return (
    <>
      <OptionList options={options} />
      {!isFreeAgent && (
        <a className={styles.footerLink} href="mailto:hq@relentless2800.com">
          <AppIcon icon="mdi:briefcase-outline" size={20} />
          Need passes for multiple teams? Contact us
        </a>
      )}
    </>
  )
}
